import { useEffect, useReducer } from 'react';
import {
  INITIALIZED,
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAIL,
  STATUS,
  RESET,
} from './constants';
import { State, Action } from './types';
import { AnchorAPI } from '../../modules/AnchorAPI';

const initialState: State = {
  error: null,
  status: STATUS.IDLE,
  featureFlags: undefined,
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case INITIALIZED:
      return {
        ...state,
        featureFlags: action.payload.featureFlags,
      };
    case FETCH:
      return { ...state, status: STATUS.LOADING, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        status: STATUS.SUCCESS,
        featureFlags: action.payload.featureFlags,
      };
    case FETCH_FAIL:
      return { ...state, status: STATUS.FAIL, error: action.payload.error };
    case RESET:
      return initialState;
    default:
      return state;
  }
}

export function useFeatureFlags(userId: number | null) {
  const isLoggedIn = !!userId;
  const [state, dispatch] = useReducer(reducer, initialState);
  const { status } = state;

  async function handleSetFeatureFlags() {
    try {
      dispatch({ type: FETCH });
      const { allEnabledFlags } = await AnchorAPI.fetchAllEnabledFeatureFlags(
        userId
      );
      const featureFlags = allEnabledFlags.map(({ name }) => name);
      dispatch({
        type: FETCH_SUCCESS,
        payload: { featureFlags },
      });
    } catch (err) {
      dispatch({
        type: FETCH_FAIL,
        payload: { error: err.message },
      });
    }
  }

  function resetFeatureFlags() {
    dispatch({ type: RESET });
  }

  useEffect(() => {
    /**
     * if user is logged in and we haven't fetched their feature flags, fetch them
     *
     * if user is NOT logged in, clear out feature flags
     */
    if (isLoggedIn) {
      if (status === STATUS.IDLE) {
        handleSetFeatureFlags();
      }
    } else {
      resetFeatureFlags();
    }
  }, [handleSetFeatureFlags, isLoggedIn, status]);

  return { handleSetFeatureFlags, state, resetFeatureFlags };
}
